/**
 * Agent Economics — admin surface for the new FinOps-for-Agents engine.
 *
 * Shows the fleet summary, recent runs, and a runaway-feed. There's also
 * a "demo run" simulator that lets a founder show what runaway detection
 * looks like in 4 clicks without any agent framework integration.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Brain, Skull, ShieldCheck, AlertTriangle, Play, Activity, TrendingUp,
} from 'lucide-react';
import CustomerROITile from '../../components/CustomerROITile';
import AgentBenchmarkTile from '../../components/AgentBenchmarkTile';

const API = process.env.REACT_APP_BACKEND_URL;

const VerdictPill = ({ verdict }) => {
  const map = {
    PROFITABLE: 'bg-emerald-500/15 text-emerald-300',
    BREAK_EVEN: 'bg-amber-500/15 text-amber-300',
    LOSS_MAKING: 'bg-rose-500/15 text-rose-300',
    NO_OUTCOME: 'bg-slate-500/15 text-slate-300',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest ${map[verdict] || map.NO_OUTCOME}`}>
      {verdict || 'NO OUTCOME'}
    </span>
  );
};

export default function AgentEconomics() {
  const [summary, setSummary] = useState(null);
  const [runs, setRuns] = useState([]);
  const [runaway, setRunaway] = useState([]);
  const [loading, setLoading] = useState(true);
  const [simBusy, setSimBusy] = useState(false);
  const [simOutput, setSimOutput] = useState(null);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    setLoading(true);
    try {
      const [sRes, rRes, rwRes] = await Promise.all([
        fetch(`${API}/api/agent-economics/summary?days=7`, { headers }),
        fetch(`${API}/api/agent-economics/runs?limit=20`, { headers }),
        fetch(`${API}/api/agent-economics/runaway-feed?limit=10`, { headers }),
      ]);
      setSummary(await sRes.json());
      setRuns((await rRes.json()).runs || []);
      setRunaway((await rwRes.json()).feed || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const runDemoSimulation = async () => {
    setSimBusy(true);
    setSimOutput(null);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      // Start a run
      const startRes = await fetch(`${API}/api/agent-economics/start`, {
        method: 'POST', headers,
        body: JSON.stringify({ agent_name: 'demo_sales_agent', hard_cap_usd: 1.50 }),
      });
      const start = await startRes.json();
      const runId = start.agent_run_id;
      // Log 3 steps that escalate
      const steps = [
        { kind: 'llm', name: 'claude-sonnet-4-5', cost_usd: 0.12 },
        { kind: 'tool', name: 'crm_lookup', cost_usd: 0.05 },
        { kind: 'llm', name: 'claude-opus-4-5', cost_usd: 0.85 },
      ];
      for (let i = 0; i < steps.length; i++) {
        await fetch(`${API}/api/agent-economics/step`, {
          method: 'POST', headers,
          body: JSON.stringify({ agent_run_id: runId, ...steps[i], parent_step_index: i }),
        });
      }
      // Pre-check budget for a hypothetical large next step
      const checkRes = await fetch(`${API}/api/agent-economics/check-budget/${runId}?next_step_estimated_usd=0.80`, {
        method: 'POST', headers,
      });
      const check = await checkRes.json();
      // Finish with $40 closed-deal outcome
      const finishRes = await fetch(`${API}/api/agent-economics/finish/${runId}`, {
        method: 'POST', headers,
        body: JSON.stringify({ outcome_value_usd: 40, outcome_label: 'closed_deal' }),
      });
      const finish = await finishRes.json();
      setSimOutput({ start, check, finish });
      refresh();
    } finally { setSimBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 -mt-px"><div className="max-w-6xl mx-auto px-6 py-8" data-testid="agent-economics-page">
      {/* Header — vibrant hero */}
      <div className="mb-10">
        <div className="flex items-start gap-4 mb-4">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-emerald-500 bg-gradient-to-br from-emerald-500 to-cyan-500 items-center justify-center shadow-xl shadow-emerald-500/40 shrink-0">
            <Brain className="w-7 h-7 text-white drop-shadow-sm" />
          </div>
          <div className="flex-1">
            <Badge className="bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 text-[10px] font-mono tracking-widest mb-2">
              WORLD-FIRST
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 drop-shadow-[0_2px_18px_rgba(16,185,129,0.25)]">
              <span className="text-white">Agent </span><span className="text-emerald-400">Economics</span>
            </h1>
          </div>
        </div>
        <p className="text-lg text-slate-600 max-w-3xl leading-relaxed">
          FinOps for autonomous AI agents. Rolling-median runaway detection, per-run ROI verdicts, and a <span className="text-emerald-300 font-semibold">pre-step hard-cap API</span> nobody else ships.
        </p>
      </div>

      {/* Summary — vibrant gradient KPI cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8" data-testid="agent-summary-cards">
          <Card className="bg-slate-950 text-slate-100 border-emerald-500/20">
            <CardContent className="p-4">
              <div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest mb-2">Runs (7d)</div>
              <div className="text-3xl font-bold text-white tabular-nums">{summary.total_runs}</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-950 text-slate-100 border-amber-400/30">
            <CardContent className="p-4">
              <div className="text-[10px] font-bold text-amber-300 uppercase tracking-widest mb-2">Runaways</div>
              <div className="text-3xl font-bold text-amber-200 tabular-nums">{summary.runaway_count} <span className="text-xs text-amber-300/70">({summary.runaway_rate_pct}%)</span></div>
            </CardContent>
          </Card>
          <Card className="bg-slate-950 text-slate-100 border-emerald-500/20">
            <CardContent className="p-4">
              <div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest mb-2">Total Cost</div>
              <div className="text-3xl font-bold text-white tabular-nums">${summary.total_cost_usd}</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-950 text-slate-100 border-emerald-400/30">
            <CardContent className="p-4">
              <div className="text-[10px] font-bold text-emerald-300 uppercase tracking-widest mb-2">Outcome Value</div>
              <div className="text-3xl font-bold text-emerald-200 tabular-nums">${summary.total_outcome_usd}</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-950 text-slate-100 border-emerald-400/40">
            <CardContent className="p-4">
              <div className="text-[10px] font-bold text-emerald-300 uppercase tracking-widest mb-2">Fleet ROI</div>
              <div className="text-3xl font-bold text-emerald-200 tabular-nums">{summary.fleet_roi_multiplier ? `${summary.fleet_roi_multiplier}×` : '—'}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Per-customer ROI tile (Feb-18-v4) */}
      <div className="mb-8">
        <CustomerROITile />
      </div>

      {/* Per-decision-kind benchmark tile (Feb-18-v4) */}
      <div className="mb-8">
        <AgentBenchmarkTile />
      </div>

      {/* Demo simulator */}
      <Card className="bg-slate-950 text-slate-100 border-emerald-500/30 mb-8" data-testid="agent-demo-sim">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center gap-2"><Play className="w-5 h-5 text-emerald-400" /> Run a demo (no SDK install)</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-400 mb-4">Simulates a 3-step sales-qualifier agent ($0.12 LLM + $0.05 tool + $0.85 LLM = $1.02), pre-checks a hypothetical $0.80 next step against a $1.50 hard cap (should BLOCK), and finishes with a $40 closed-deal outcome.</p>
          <Button onClick={runDemoSimulation} disabled={simBusy} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="run-demo-sim-btn">
            {simBusy ? 'Running…' : 'Run demo simulation'}
          </Button>
          {simOutput && (
            <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
              <div className="rounded-md bg-slate-950 border border-emerald-500/20 p-3">
                <div className="font-bold text-emerald-300 mb-1">1. start</div>
                <div className="text-slate-400">run_id: <span className="text-white font-mono">{simOutput.start.agent_run_id}</span></div>
                <div className="text-slate-400">hard_cap: <span className="text-white">${simOutput.start.hard_cap_usd}</span></div>
              </div>
              <div className={`rounded-md border p-3 ${simOutput.check.kill_recommended ? 'bg-rose-500/10 border-rose-500/30' : 'bg-emerald-500/10 border-emerald-500/30'}`}>
                <div className="font-bold text-amber-300 mb-1">2. check-budget</div>
                <div className="text-slate-400">allow: <span className="font-bold">{String(simOutput.check.allow)}</span></div>
                <div className="text-slate-400">kill_recommended: <span className="font-bold">{String(simOutput.check.kill_recommended)}</span></div>
                <div className="text-slate-400 text-[10px] mt-1">{simOutput.check.reason}</div>
              </div>
              <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 p-3">
                <div className="font-bold text-emerald-300 mb-1">3. finish</div>
                <div className="text-slate-400">cost: ${simOutput.finish.total_cost_usd}</div>
                <div className="text-slate-400">outcome: ${simOutput.finish.outcome_value_usd}</div>
                <div className="text-slate-400">ROI: <span className="font-bold">{simOutput.finish.roi_multiplier}×</span></div>
                <div className="mt-1"><VerdictPill verdict={simOutput.finish.verdict} /></div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Runaway feed */}
      <Card className="bg-slate-950 text-slate-100 border-rose-500/30 mb-8">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center gap-2"><Skull className="w-5 h-5 text-rose-400" /> Runaway feed <span className="text-xs text-cyan-300 font-normal">({runaway.length})</span></CardTitle>
        </CardHeader>
        <CardContent>
          {runaway.length === 0 ? (
            <div className="text-center py-6 text-sm text-slate-400">
              No runaway agents detected. Need ≥5 completed runs for the same agent name to enable baseline detection.
            </div>
          ) : (
            <div className="space-y-2">
              {runaway.map((r, i) => (
                <div key={i} className="flex items-center justify-between rounded-md border border-rose-500/30 bg-rose-500/5 px-3 py-2 text-sm">
                  <div>
                    <div className="font-medium text-white">{r.agent_name}</div>
                    <div className="text-xs text-rose-300/80">{r.runaway_reason}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-white font-mono">${r.total_cost_usd}</div>
                    <div className="text-xs text-slate-400">{r.step_count} steps</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent runs */}
      <Card className="bg-slate-950 text-slate-100 border-emerald-500/20">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center gap-2"><Activity className="w-5 h-5 text-cyan-400" /> Recent runs <span className="text-xs text-cyan-300 font-normal">({runs.length})</span></CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">{[0, 1, 2].map(i => <div key={i} className="h-10 bg-slate-800 rounded animate-pulse" />)}</div>
          ) : runs.length === 0 ? (
            <div className="text-center py-6 text-sm text-slate-400">No agent runs yet. Click "Run demo simulation" above to seed one.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase text-cyan-300 border-b border-slate-800">
                  <tr><th className="text-left py-2">Agent</th><th className="text-right">Cost</th><th className="text-right">Steps</th><th className="text-right">Outcome</th><th className="text-right">ROI</th><th className="text-right">Status</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {runs.map((r, i) => (
                    <tr key={i} className="hover:bg-slate-800">
                      <td className="py-2 text-white">{r.agent_name}</td>
                      <td className="py-2 text-right font-mono text-slate-400">${(r.total_cost_usd || 0).toFixed(4)}</td>
                      <td className="py-2 text-right text-slate-400">{r.step_count}</td>
                      <td className="py-2 text-right text-emerald-300">${(r.outcome_value_usd || 0).toFixed(2)}</td>
                      <td className="py-2 text-right text-white">{r.roi_multiplier ? `${r.roi_multiplier}×` : '—'}</td>
                      <td className="py-2 text-right">{r.is_runaway ? <span className="text-rose-300 text-xs"><AlertTriangle className="w-3 h-3 inline" /> runaway</span> : <span className="text-emerald-400 text-xs"><ShieldCheck className="w-3 h-3 inline" /> ok</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
